// Phase 1.5 item #2A · Datasets pages — restyled to the Eval-Execution Engine
// mockup (docs/design/eval-execution-engine-mockup-v1.html, Screen 0 · Golden
// set). Presentation renders entirely through EvalUI.jsx; every data path,
// API call, and format-helper contract is unchanged (logic stays in the
// 100%-covered datasetItemFormat.js unit).
//
// Backend routes:
//   GET    /api/observability/datasets               → list
//   POST   /api/observability/datasets               → create (admin)
//   POST   /api/observability/datasets/{id}/items    → add trace (admin)
//   GET    /api/observability/datasets/{id}/runs     → list runs
//
// RBAC per spec §6: create = admin/developer, list = any role with
// Inspector access. Server-side enforces; UI hides actions for
// roles that can't perform them.
import React, { useEffect, useRef, useState } from "react";
import { COLORS } from "../../../theme/colors";
import { Button, Input, Modal } from "../../../components/ui";
import { observabilityApi } from "../../../api/client";
import { useAuth } from "../../../contexts/AuthContext";
import { EmptyState, ErrorState, LoadingRow } from "./SharedUI";
import { humanizeError as _humanizeError } from "../../../utils/humanizeError";
import { buildRunDetailId } from "./evalRunFormat";
import { parseGoldenFile, validateItemDraft, normalizeItems } from "./datasetItemFormat";
import {
  SectionHead, Toolbar, Spacer, Hint, Btn, Panel, Dropzone, Callout, B, Mono,
  TableCard, THead, Tr, Td, QCell, SourcePill, StatusPill, BackButton,
} from "./EvalUI";

// MNT-014: shared humanizer + this page's status-specific overrides.
const humanizeError = (e) => _humanizeError(e, {
  500: "Langfuse query failed. Try refresh.",
  403: "You don't have permission to perform this action.",
});

const ADMIN_ROLES = ["owner", "administrator", "developer"];

// Textarea + label styling matching the mockup's `.ta` / `.formhead`.
const taStyle = {
  font: "inherit", fontSize: 13, color: COLORS.text, background: COLORS.surface,
  border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "9px 11px",
  resize: "vertical", minHeight: 62, width: "100%", boxSizing: "border-box",
};
const formHead = {
  fontSize: 10, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase",
  color: COLORS.textDim, marginBottom: 5, display: "block",
};

export function DatasetsListPage({ navigate }) {
  const { currentUser } = useAuth();
  const canCreate = ADMIN_ROLES.includes(currentUser?.role || "");
  const [datasets, setDatasets] = useState(null);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setError(null);
    try {
      const res = await observabilityApi.listDatasets();
      if (res?.success === false) setError(res.error || "Failed to load");
      else setDatasets(res?.data || res || []);
    } catch (e) {
      setError(humanizeError(e));
      setDatasets([]);
    }
  };
  useEffect(() => { load(); }, []);

  const onCreate = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      const res = await observabilityApi.createDataset(newName.trim(), newDesc.trim() || null);
      if (res?.success === false) {
        setError(res.error || "Create failed");
      } else {
        setCreating(false); setNewName(""); setNewDesc("");
        await load();
      }
    } catch (e) {
      setError(humanizeError(e));
    } finally {
      setBusy(false);
    }
  };

  if (datasets === null && !error) return <LoadingRow label="Loading datasets…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  const items = Array.isArray(datasets) ? datasets : (datasets?.data || []);

  return (
    <div>
      <SectionHead title="Datasets" count={`${items.length} set${items.length === 1 ? "" : "s"}`}>
        A dataset is a <B>golden set</B> — a list of question → expected-answer pairs.
        Run an agent over it and score every answer against the ground truth.
      </SectionHead>

      <Toolbar>
        {canCreate && <Btn onClick={() => setCreating(true)}>＋ New dataset</Btn>}
      </Toolbar>

      {items.length === 0 ? (
        <EmptyState
          title="No datasets yet"
          detail={canCreate
            ? "Create one to start collecting golden Q&A pairs for evaluation."
            : "An administrator can create the first dataset."}
        />
      ) : (
        <TableCard title="Golden sets" meta={`${items.length} · tenant-scoped`} minWidth={640}>
          <THead cols={["Name", "Items", "Description", "Created"]} />
          <tbody>
            {items.map((d) => (
              <Tr key={d.id} onClick={() => navigate(`/analytics/observability/datasets/${d.id}`)}>
                <Td><span style={{ color: COLORS.text, fontWeight: 600 }}>{d.name || d.id}</span></Td>
                <Td mono>{d.item_count ?? d.items_count ?? 0}</Td>
                <Td>{(d.description || "—").slice(0, 80)}</Td>
                <Td mono>{d.created_at ? new Date(d.created_at).toLocaleDateString() : "—"}</Td>
              </Tr>
            ))}
          </tbody>
        </TableCard>
      )}

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="New dataset"
        width={520}
        footer={
          <div style={{ display: "flex", gap: 8 }}>
            <Button variant="secondary" onClick={() => setCreating(false)}>Cancel</Button>
            <Button onClick={onCreate} disabled={!newName.trim() || busy} loading={busy}>
              Create
            </Button>
          </div>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Input
            label="Name"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="contract-qa-golden-set"
            autoFocus
          />
          <Input
            label="Description (optional)"
            value={newDesc}
            onChange={e => setNewDesc(e.target.value)}
            placeholder="Curated Q&A pairs for legal-contract agent eval"
          />
        </div>
      </Modal>
    </div>
  );
}

export function DatasetDetailPage({ detailId, navigate }) {
  const { currentUser } = useAuth();
  const canMutate = ADMIN_ROLES.includes(currentUser?.role || "");
  const [view, setView] = useState("items");
  const [meta, setMeta] = useState(null);

  // Fetch the dataset's display name once (nice header instead of a raw cuid).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await observabilityApi.listDatasets();
        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        const d = list.find((x) => x.id === detailId);
        if (alive && d) setMeta(d);
      } catch { /* header falls back to the id */ }
    })();
    return () => { alive = false; };
  }, [detailId]);

  const title = meta?.name || `Dataset · ${String(detailId || "").slice(0, 12)}…`;

  return (
    <div>
      <BackButton label="Datasets" onClick={() => navigate("/analytics/observability/datasets")} />

      {/* Sub-tabs — Golden set (items) vs Eval runs */}
      <div style={{ display: "flex", gap: 2, marginBottom: 16, borderBottom: `1px solid ${COLORS.border}` }}>
        {[["items", "Golden set"], ["runs", "Eval runs"]].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setView(id)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              padding: "9px 13px", fontSize: 13, fontWeight: 600,
              color: view === id ? COLORS.text : COLORS.textMuted,
              borderBottom: `2px solid ${view === id ? COLORS.accent : "transparent"}`,
            }}
          >{label}</button>
        ))}
      </div>

      {view === "items"
        ? <DatasetItemsView datasetId={detailId} datasetTitle={title} itemCount={meta?.item_count ?? meta?.items_count} canMutate={canMutate} />
        : <DatasetRunsView datasetId={detailId} navigate={navigate} />}
    </div>
  );
}

// ─── Golden-set Items view (Screen 0) ────────────────────────────────
// Exported for the N2 render test: before the dataset-scoping fix every
// dataset showed at least one (leaked) item, so the empty-golden-set path had
// never actually rendered. It does now, and must not crash.
export function DatasetItemsView({ datasetId, datasetTitle, itemCount, canMutate }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [draft, setDraft] = useState({ input: "", expected_output: "" });
  const [draftErr, setDraftErr] = useState(null);
  const [busy, setBusy] = useState(false);
  // Inline edit + delete.
  const [editId, setEditId] = useState(null);
  const [editDraft, setEditDraft] = useState({ input: "", expected_output: "" });
  const [deleteId, setDeleteId] = useState(null);
  const questionRef = useRef(null);

  const load = async () => {
    setError(null);
    try {
      const res = await observabilityApi.listDatasetItems(datasetId);
      if (res?.success === false) setError(res.error || "Failed to load");
      else setItems(normalizeItems(res));
    } catch (e) {
      setError(humanizeError(e));
      setItems([]);
    }
  };
  useEffect(() => { load(); }, [datasetId]);

  const onSaveNew = async () => {
    const msg = validateItemDraft(draft);
    if (msg) { setDraftErr(msg); return; }
    setBusy(true); setDraftErr(null);
    try {
      const res = await observabilityApi.createDatasetItem(
        datasetId, draft.input.trim(), draft.expected_output.trim() || null,
      );
      if (res?.success === false) { setDraftErr(res.error || "Save failed"); }
      else {
        setDraft({ input: "", expected_output: "" });
        await load();
      }
    } catch (e) {
      setDraftErr(humanizeError(e));
    } finally { setBusy(false); }
  };

  const startEdit = (it) => {
    setEditId(it.id);
    setEditDraft({ input: it.input || "", expected_output: it.expected_output || "" });
  };
  const onSaveEdit = async (itemId) => {
    const msg = validateItemDraft(editDraft);
    if (msg) { setDraftErr(msg); return; }
    setBusy(true); setDraftErr(null);
    try {
      const res = await observabilityApi.updateDatasetItem(datasetId, itemId, {
        input: editDraft.input.trim(),
        expected_output: editDraft.expected_output,
      });
      if (res?.success === false) { setDraftErr(res.error || "Update failed"); }
      else { setEditId(null); await load(); }
    } catch (e) {
      setDraftErr(humanizeError(e));
    } finally { setBusy(false); }
  };
  const onDelete = async (itemId) => {
    setBusy(true);
    try {
      const res = await observabilityApi.deleteDatasetItem(datasetId, itemId);
      if (res?.success === false) setError(res.error || "Delete failed");
      setDeleteId(null);
      await load();
    } catch (e) {
      setError(humanizeError(e));
    } finally { setBusy(false); }
  };

  if (items === null && !error) return <LoadingRow label="Loading golden set…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  const count = items.length || itemCount || 0;

  return (
    <div>
      <SectionHead title={datasetTitle} count={`${count} item${count === 1 ? "" : "s"}`}>
        A golden set is a list of <B>question → expected answer</B> pairs. The eval
        engine runs the agent on each question and scores its answer against the
        expected one. Build a set three ways:
      </SectionHead>

      {canMutate && (
        <Toolbar>
          <Btn onClick={() => questionRef.current?.focus()}>＋ Add item</Btn>
          <Btn variant="ghost" onClick={() => setShowImport(true)}>⬆ Import CSV / JSONL</Btn>
          <Spacer />
          <Hint>…or capture a real conversation from the <B accent>Inspector</B> → "Add to golden set".</Hint>
        </Toolbar>
      )}

      {/* Inline author-from-scratch form (always visible, per the mockup) */}
      {canMutate && (
        <Panel>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <span style={formHead}>Question (input the agent receives)</span>
              <textarea
                ref={questionRef}
                style={taStyle}
                value={draft.input}
                onChange={e => setDraft(d => ({ ...d, input: e.target.value }))}
                placeholder="e.g. What is the average interest rate for approved loans in Q3?"
              />
            </div>
            <div>
              <span style={formHead}>Expected answer (ground truth)</span>
              <textarea
                style={taStyle}
                value={draft.expected_output}
                onChange={e => setDraft(d => ({ ...d, expected_output: e.target.value }))}
                placeholder="The correct answer the agent should produce."
              />
            </div>
          </div>
          {draftErr && <div style={{ fontSize: 11, color: COLORS.pink, marginTop: 8 }}>{draftErr}</div>}
          <div style={{ display: "flex", gap: 10, marginTop: 12, alignItems: "center" }}>
            <Btn onClick={onSaveNew} disabled={busy}>Save item</Btn>
            <Btn variant="ghost" onClick={() => { setDraft({ input: "", expected_output: "" }); setDraftErr(null); }}>Clear</Btn>
            <Hint style={{ maxWidth: "44ch" }}>Numeric expected answers are scored deterministically; prose is scored by the BYOK judge.</Hint>
          </div>
        </Panel>
      )}

      {/* Bulk-import affordance */}
      {canMutate && (
        <Dropzone onClick={() => setShowImport(true)} style={{ marginBottom: 16 }}>
          <B>Import CSV / JSONL</B> — columns <Mono>question, expected_answer</Mono> (CSV) or{" "}
          <Mono>{`{"input":…, "expected_output":…}`}</Mono> per line (JSONL). A preview shows the parsed rows before they're saved.
        </Dropzone>
      )}

      {items.length === 0 ? (
        <EmptyState
          title="No items yet"
          detail={canMutate
            ? "Author a question + expected answer above, import a CSV/JSONL, or capture from the Inspector."
            : "An administrator can add golden Q&A pairs to this dataset."}
        />
      ) : (
        <TableCard
          title="Items in this set"
          meta={`${items.length} item${items.length === 1 ? "" : "s"}${canMutate ? " · editable" : ""}`}
          minWidth={720}
        >
          <THead cols={["#", "Question (input)", "Expected answer", "Source", { label: "Edit", align: "right" }]} />
          <tbody>
            {items.map((it, i) => (
              editId === it.id ? (
                <tr key={it.id}>
                  <Td colSpan={5} style={{ background: COLORS.surfaceHover }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <div>
                        <span style={formHead}>Question (input)</span>
                        <textarea style={taStyle} value={editDraft.input}
                          onChange={e => setEditDraft(d => ({ ...d, input: e.target.value }))} />
                      </div>
                      <div>
                        <span style={formHead}>Expected answer</span>
                        <textarea style={taStyle} value={editDraft.expected_output}
                          onChange={e => setEditDraft(d => ({ ...d, expected_output: e.target.value }))} />
                      </div>
                    </div>
                    {draftErr && <div style={{ fontSize: 11, color: COLORS.pink, marginTop: 8 }}>{draftErr}</div>}
                    <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                      <Btn onClick={() => onSaveEdit(it.id)} disabled={busy}>Save</Btn>
                      <Btn variant="ghost" onClick={() => { setEditId(null); setDraftErr(null); }}>Cancel</Btn>
                    </div>
                  </Td>
                </tr>
              ) : (
                <Tr key={it.id}>
                  <Td first mono>{i + 1}</Td>
                  <Td><QCell maxWidth={360}>{it.input || "—"}</QCell></Td>
                  <Td>
                    <span style={{ color: COLORS.text, display: "block", maxWidth: 340, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {it.expected_output || "—"}
                    </span>
                  </Td>
                  <Td><SourcePill source={it.source} /></Td>
                  <Td align="right">
                    {canMutate && (
                      <span style={{ display: "inline-flex", gap: 8 }}>
                        <button title="Edit" onClick={() => startEdit(it)}
                          style={{ font: "inherit", fontSize: 12, color: COLORS.textMuted, background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "4px 9px", cursor: "pointer" }}>✎</button>
                        <button title="Delete" onClick={() => setDeleteId(it.id)}
                          style={{ font: "inherit", fontSize: 12, color: COLORS.textMuted, background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "4px 9px", cursor: "pointer" }}>🗑</button>
                      </span>
                    )}
                  </Td>
                </Tr>
              )
            ))}
          </tbody>
        </TableCard>
      )}

      <Callout>
        <B>Three ways to build the set:</B> <B>Author</B> — type a question + expected answer inline.{" "}
        <B>Import</B> — upload a CSV/JSONL from a spreadsheet (fastest for a large set).{" "}
        <B>Capture</B> — from a real conversation in the Inspector, click "Add to golden set" and edit
        the expected answer. Every item is editable here; the <SourcePill source="authored" /> tag shows where each came from.
      </Callout>

      <ImportItemsModal
        open={showImport}
        onClose={() => setShowImport(false)}
        datasetId={datasetId}
        onImported={load}
      />

      <Modal
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        title="Delete item?"
        width={420}
        footer={
          <div style={{ display: "flex", gap: 8 }}>
            <Button variant="secondary" onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button onClick={() => onDelete(deleteId)} disabled={busy} loading={busy}
                    style={{ background: "#dc2626" }}>Delete</Button>
          </div>
        }
      >
        <div style={{ fontSize: 12, color: COLORS.textMuted }}>
          This removes the Q&A pair from the golden set. Eval runs already recorded are unaffected.
        </div>
      </Modal>
    </div>
  );
}

// Import modal — file/paste → parseGoldenFile preview → importDatasetItems.
function ImportItemsModal({ open, onClose, datasetId, onImported }) {
  const [content, setContent] = useState("");
  const [filename, setFilename] = useState("");
  const [preview, setPreview] = useState({ rows: [], error: null });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [serverErr, setServerErr] = useState(null);

  const reset = () => {
    setContent(""); setFilename(""); setPreview({ rows: [], error: null });
    setResult(null); setServerErr(null);
  };
  const close = () => { reset(); onClose(); };

  const ingest = (text, name) => {
    setContent(text); setFilename(name);
    setPreview(parseGoldenFile(text, name));
    setResult(null); setServerErr(null);
  };
  const onFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => ingest(String(reader.result || ""), f.name);
    reader.readAsText(f);
  };

  const onSave = async () => {
    setBusy(true); setServerErr(null);
    try {
      const res = await observabilityApi.importDatasetItems(datasetId, content, filename || "import.csv");
      if (res?.success === false) { setServerErr(res.error || "Import failed"); }
      else {
        setResult(res?.data || res);
        await onImported();
      }
    } catch (e) {
      setServerErr(humanizeError(e));
    } finally { setBusy(false); }
  };

  const canSave = preview.rows.length > 0 && !preview.error && !busy;

  return (
    <Modal
      open={open}
      onClose={close}
      title="Import golden-set items"
      width={640}
      footer={
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="secondary" onClick={close}>Close</Button>
          <Button onClick={onSave} disabled={!canSave} loading={busy}>
            Save {preview.rows.length || ""} item{preview.rows.length === 1 ? "" : "s"}
          </Button>
        </div>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ fontSize: 11, color: COLORS.textMuted }}>
          CSV columns <Mono>question, expected_answer</Mono> — or JSONL with{" "}
          <Mono>{`{"input": …, "expected_output": …}`}</Mono> per line. Drop a file or paste below;
          a preview shows the parsed rows before they're saved.
        </div>
        <input type="file" accept=".csv,.jsonl,.ndjson,.txt" onChange={onFile}
               style={{ fontSize: 12 }} />
        <textarea
          style={{ ...taStyle, minHeight: 110 }}
          value={content}
          onChange={e => ingest(e.target.value, filename || "pasted.csv")}
          placeholder={"question,expected_answer\nWhat is 2+2?,4"}
        />

        {preview.error && (
          <div style={{ fontSize: 11, color: COLORS.pink }}>⚠ {preview.error}</div>
        )}
        {preview.rows.length > 0 && (
          <div>
            <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6 }}>
              Preview · {preview.rows.length} row{preview.rows.length === 1 ? "" : "s"}
            </div>
            <div style={{ maxHeight: 200, overflowY: "auto" }}>
              <TableCard title="Parsed rows" meta={`${preview.rows.length}`} minWidth={0}>
                <THead cols={["Question", "Expected answer"]} />
                <tbody>
                  {preview.rows.slice(0, 50).map((r, i) => (
                    <Tr key={i}>
                      <Td><span style={{ color: COLORS.text }}>{r.input}</span></Td>
                      <Td>{r.expected_output || "—"}</Td>
                    </Tr>
                  ))}
                </tbody>
              </TableCard>
            </div>
          </div>
        )}

        {serverErr && <div style={{ fontSize: 11, color: COLORS.pink }}>⚠ {serverErr}</div>}
        {result && (
          <div style={{ fontSize: 12, color: COLORS.text }}>
            Imported <b>{result.created ?? 0}</b> item{(result.created ?? 0) === 1 ? "" : "s"}
            {result.failed ? <span style={{ color: COLORS.pink }}> · {result.failed} failed</span> : null}
            {Array.isArray(result.errors) && result.errors.length > 0 && (
              <ul style={{ margin: "6px 0 0", paddingLeft: 18, color: COLORS.pink, fontSize: 11 }}>
                {result.errors.slice(0, 10).map((er, i) => <li key={i}>{String(er)}</li>)}
              </ul>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─── Eval runs view — recent runs for this dataset ───────────────────
function DatasetRunsView({ datasetId, navigate }) {
  const [runs, setRuns] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setError(null);
    try {
      const res = await observabilityApi.listDatasetRuns(datasetId);
      if (res?.success === false) setError(res.error || "Failed to load");
      else setRuns(res?.data || res || []);
    } catch (e) {
      setError(humanizeError(e));
      setRuns([]);
    }
  };
  useEffect(() => { load(); }, [datasetId]);

  if (runs === null && !error) return <LoadingRow label="Loading dataset runs…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  const items = Array.isArray(runs) ? runs : (runs?.data || []);

  return (
    <div>
      <SectionHead title="Eval runs" count={`${items.length}`}>
        Every run executes the agent over this set, scores each answer, and saves the aggregate
        pass/fail/accuracy. Start a run from the Evals tab.
      </SectionHead>

      {items.length === 0 ? (
        <EmptyState
          title="No runs yet"
          detail="Run an eval against this dataset from the Evals tab or via the API."
        />
      ) : (
        <TableCard title="Runs against this dataset" meta={`${items.length}`} minWidth={620}>
          <THead cols={["Run", "Items", "Started", "Status"]} />
          <tbody>
            {items.map((r) => (
              <Tr
                key={r.id}
                onClick={() => navigate(`/analytics/observability/evals/${buildRunDetailId(datasetId, r.run_id || r.id, r.name || r.run_name || r.id)}`)}
              >
                <Td mono style={{ color: COLORS.text }}>{(r.name || r.run_name || r.id || "").slice(0, 32)}</Td>
                <Td mono>{r.item_count ?? r.items_count ?? "—"}</Td>
                <Td mono>{r.started_at ? new Date(r.started_at).toLocaleString() : "—"}</Td>
                <Td><StatusPill state={r.status} /></Td>
              </Tr>
            ))}
          </tbody>
        </TableCard>
      )}
    </div>
  );
}
